/**
 * @module localStorage
 * @description Implements the local-first database adapter used for demos,
 * prototyping, and Storybook scenarios. The adapter stores collections in
 * browser `localStorage` while conforming to the shared database contract.
 */

import {
  getBrowserLocalStorage,
  hasBrowserLocalStorage,
  isBrowserStorageQuotaError,
} from "../browser-storage";
import { DbError } from "../errors";
import { warnRuntimeAdapterMock } from "../runtime-warning";
import type { ListOptions, QueryCondition, QueryFilter } from "../types";
import type { DbAdapter } from "./interface";

const DB_PREFIX = "geenius_db_";
const QUERY_OPERATORS = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "in",
  "contains",
] as const;

type StoredDbRecord = Record<string, unknown> & { id: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStoredDbRecord(value: unknown): value is StoredDbRecord {
  return isRecord(value) && typeof value.id === "string";
}

function isQueryOperator(value: unknown): value is QueryCondition["operator"] {
  return (
    typeof value === "string" &&
    (QUERY_OPERATORS as readonly string[]).includes(value)
  );
}

function isComparableValue(value: unknown): value is number | string {
  return typeof value === "number" || typeof value === "string";
}

function isQueryCondition(value: unknown): value is QueryCondition {
  if (
    !isRecord(value) ||
    typeof value.field !== "string" ||
    value.field.length === 0 ||
    !isQueryOperator(value.operator)
  ) {
    return false;
  }

  switch (value.operator) {
    case "eq":
    case "neq":
      return true;
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      return isComparableValue(value.value);
    case "in":
      return Array.isArray(value.value);
    case "contains":
      return typeof value.value === "string";
  }
}

function validateQueryFilter(filter: unknown): QueryFilter {
  if (!Array.isArray(filter) || !filter.every(isQueryCondition)) {
    throw new DbError("Invalid query filter", "QUERY_ERROR");
  }
  return filter;
}

function parseCollectionData(raw: string | null): StoredDbRecord[] {
  const parsed: unknown = JSON.parse(raw || "[]");
  if (!Array.isArray(parsed) || !parsed.every(isStoredDbRecord)) {
    throw new DbError("Invalid localStorage collection payload", "QUERY_ERROR");
  }
  return parsed;
}

function compareValues(
  left: unknown,
  right: unknown,
  operator: Extract<QueryCondition["operator"], "gt" | "gte" | "lt" | "lte">,
): boolean {
  if (typeof left === "number" && typeof right === "number") {
    switch (operator) {
      case "gt":
        return left > right;
      case "gte":
        return left >= right;
      case "lt":
        return left < right;
      case "lte":
        return left <= right;
    }
  }

  if (typeof left === "string" && typeof right === "string") {
    switch (operator) {
      case "gt":
        return left > right;
      case "gte":
        return left >= right;
      case "lt":
        return left < right;
      case "lte":
        return left <= right;
    }
  }

  return false;
}

function castStoredItem<T>(item: StoredDbRecord): T;
function castStoredItem(item: StoredDbRecord): unknown {
  // This adapter is intentionally schema-less; callers provide the concrete
  // collection shape through `T` when records are read back out.
  return item;
}

function castStoredItems<T>(items: StoredDbRecord[]): T[];
function castStoredItems(items: StoredDbRecord[]): unknown[] {
  // The persisted payload is validated to contain JSON objects with string ids;
  // the rest of each record remains caller-defined collection data.
  return items;
}

function getCollection(collection: string): StoredDbRecord[] {
  try {
    const storage = getBrowserLocalStorage();
    return parseCollectionData(storage.getItem(DB_PREFIX + collection));
  } catch (error) {
    if (error instanceof DbError) {
      throw error;
    }
    if (error instanceof SyntaxError) {
      throw new DbError(
        "Failed to parse localStorage data",
        "QUERY_ERROR",
        error,
      );
    }
    throw new DbError("Failed to read from localStorage", "QUERY_ERROR", error);
  }
}

function saveCollection(collection: string, data: StoredDbRecord[]): void {
  try {
    const storage = getBrowserLocalStorage();
    storage.setItem(DB_PREFIX + collection, JSON.stringify(data));
  } catch (error) {
    if (isBrowserStorageQuotaError(error)) {
      throw new DbError(
        "localStorage quota exceeded",
        "CONNECTION_ERROR",
        error,
      );
    }
    throw new DbError("Failed to write to localStorage", "QUERY_ERROR", error);
  }
}

function matchesCondition(item: StoredDbRecord, cond: QueryCondition): boolean {
  const val = item[cond.field];
  switch (cond.operator) {
    case "eq":
      return val === cond.value;
    case "neq":
      return val !== cond.value;
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      return compareValues(val, cond.value, cond.operator);
    case "in":
      return Array.isArray(cond.value) && cond.value.includes(val);
    case "contains":
      return (
        typeof val === "string" &&
        typeof cond.value === "string" &&
        val.includes(cond.value)
      );
    default:
      return false;
  }
}

function matchesFilter(item: StoredDbRecord, filter: QueryFilter): boolean {
  return filter.every((cond) => matchesCondition(item, cond));
}

function applyListOptions(
  data: StoredDbRecord[],
  options?: ListOptions,
): StoredDbRecord[] {
  let items = [...data];

  if (options?.orderBy) {
    const { orderBy } = options;
    const dir = options.order === "desc" ? -1 : 1;
    items.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      if (typeof av === "number" && typeof bv === "number") {
        return av < bv ? -dir : av > bv ? dir : 0;
      }
      if (typeof av === "string" && typeof bv === "string") {
        return av < bv ? -dir : av > bv ? dir : 0;
      }
      return 0;
    });
  }

  if (options?.offset) items = items.slice(options.offset);
  if (options?.limit) items = items.slice(0, options.limit);
  return items;
}

// Inline SSR fallback — a minimal in-memory store for when window/localStorage
// is not available (SSR). The full memory adapter lives in @geenius/db.
function createServerSideFallback(): DbAdapter {
  const store = new Map<string, StoredDbRecord[]>();
  const getCol = (c: string) => store.get(c) || [];
  const setCol = (c: string, d: StoredDbRecord[]) => {
    store.set(c, d);
  };

  return {
    async create<T extends Record<string, unknown>>(
      c: string,
      data: Omit<T, "id">,
    ) {
      const items = getCol(c);
      const item: StoredDbRecord = { ...data, id: crypto.randomUUID() };
      items.push(item);
      setCol(c, items);
      return castStoredItem<T & { id: string }>(item);
    },
    async get<T>(c: string, id: string) {
      const item = getCol(c).find((i) => i.id === id);
      return item ? castStoredItem<T>(item) : null;
    },
    async update<T extends Record<string, unknown>>(
      c: string,
      id: string,
      data: Partial<T>,
    ) {
      const items = getCol(c);
      const idx = items.findIndex((i) => i.id === id);
      if (idx === -1) return null;
      const item = items[idx];
      if (!item) return null;
      Object.assign(item, data);
      setCol(c, items);
      return castStoredItem<T>(item);
    },
    async delete(c: string, id: string) {
      const items = getCol(c);
      const idx = items.findIndex((i) => i.id === id);
      if (idx === -1) return false;
      items.splice(idx, 1);
      setCol(c, items);
      return true;
    },
    async list<T>(c: string, options?: ListOptions) {
      return castStoredItems<T>(applyListOptions(getCol(c), options));
    },
    async query<T>(c: string, filter: QueryFilter) {
      const validFilter = validateQueryFilter(filter);
      return castStoredItems<T>(
        getCol(c).filter((item) => matchesFilter(item, validFilter)),
      );
    },
    async count(c: string, filter?: QueryFilter) {
      const items = getCol(c);
      if (!filter || filter.length === 0) {
        return items.length;
      }
      const validFilter = validateQueryFilter(filter);
      return validFilter.length === 0
        ? items.length
        : items.filter((i) => matchesFilter(i, validFilter)).length;
    },
  };
}

/**
 * Creates the mock localStorage-backed database adapter used in demos and Storybook.
 *
 * @returns A database adapter implementation that persists collections to browser storage.
 * @throws {DbError} When collection reads or writes fail during localStorage access.
 */
export function createLocalStorageDbAdapter(): DbAdapter {
  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "production"
  ) {
    warnRuntimeAdapterMock(
      "[@geenius/adapters] localStorage DB adapter is a MOCK for prototyping only. " +
        "Do NOT use in production. Use a real DB provider from @geenius/db instead.",
    );
  }

  if (!hasBrowserLocalStorage()) {
    return createServerSideFallback();
  }

  return {
    async create<T extends Record<string, unknown>>(
      collection: string,
      data: Omit<T, "id">,
    ) {
      const items = getCollection(collection);
      const item: StoredDbRecord = { ...data, id: crypto.randomUUID() };
      items.push(item);
      saveCollection(collection, items);
      return castStoredItem<T & { id: string }>(item);
    },

    async get<T>(collection: string, id: string) {
      const found = getCollection(collection).find((item) => item.id === id);
      return found ? castStoredItem<T>(found) : null;
    },

    async update<T extends Record<string, unknown>>(
      collection: string,
      id: string,
      data: Partial<T>,
    ) {
      const items = getCollection(collection);
      const idx = items.findIndex((item) => item.id === id);
      if (idx === -1) return null;
      const item = items[idx];
      if (!item) return null;
      Object.assign(item, data);
      saveCollection(collection, items);
      return castStoredItem<T>(item);
    },

    async delete(collection: string, id: string) {
      const items = getCollection(collection);
      const idx = items.findIndex((item) => item.id === id);
      if (idx === -1) return false;
      items.splice(idx, 1);
      saveCollection(collection, items);
      return true;
    },

    async list<T>(collection: string, options?: ListOptions) {
      return castStoredItems<T>(
        applyListOptions(getCollection(collection), options),
      );
    },

    async query<T>(collection: string, filter: QueryFilter) {
      const validFilter = validateQueryFilter(filter);
      return castStoredItems<T>(
        getCollection(collection).filter((item) =>
          matchesFilter(item, validFilter),
        ),
      );
    },

    async count(collection: string, filter?: QueryFilter) {
      const items = getCollection(collection);
      if (!filter || filter.length === 0) return items.length;
      const validFilter = validateQueryFilter(filter);
      return items.filter((item) => matchesFilter(item, validFilter)).length;
    },
  };
}
